/**
 * Broadcast Notifikasi In-App (mig 138 + 140) — helper murni & tipe.
 *
 * Admin menyiarkan satu pemberitahuan ke bell/`notifications` pembeli untuk
 * audiens terpilih. Resolusi audiens OTORITATIF terjadi di server (RPC
 * `admin_broadcast_notification`); helper di sini hanya untuk:
 *   • PREVIEW jumlah & daftar penerima di UI (cermin server),
 *   • validasi draft sebelum kirim,
 *   • sanitasi link tujuan (deep-link internal saja).
 *
 * Dipisah dari wrapper Supabase (`src/lib/supabase/broadcast.ts`) supaya bisa
 * diuji di node tanpa DOM. Pola cermin `src/lib/waTemplates.ts`
 * (`filterBroadcastRecipients`).
 */

export type BroadcastAudience =
  | "all"
  | "by_group"
  | "by_handler"
  | "by_level"
  | "specific";

/** Level pelanggan (enum `customer_level`) untuk audiens "per level". */
export const LEVEL_OPTIONS = ["Customer", "Reseller", "Agen"] as const;

/** Baris pelanggan untuk preview penerima (cermin kolom server). */
export type BroadcastTarget = {
  id: string;
  name: string | null;
  storeName?: string | null;
  phone?: string | null;
  status?: string | null;
  groupId?: string | null;
  customerLevel?: string | null;
  assignedAdminId?: string | null;
  /** Pelanggan tanpa akun auth tak bisa login → tak akan melihat notif. */
  authUserId?: string | null;
};

export type BroadcastFilter = {
  audience: BroadcastAudience;
  groupId?: string | null;
  adminId?: string | null;
  level?: string | null;
  customerIds?: string[] | null;
};

export type BroadcastDraft = {
  audience: BroadcastAudience;
  title: string;
  body: string;
  link?: string | null;
  groupId?: string | null;
  adminId?: string | null;
  level?: string | null;
  customerIds?: string[] | null;
  /** Bypass opt-out tampilan pembeli (pengumuman penting/kritis). */
  important?: boolean;
  /** ISO/`datetime-local`; bila di masa depan → broadcast dijadwalkan. */
  scheduledAt?: string | null;
};

export const BROADCAST_AUDIENCE_LABEL: Record<BroadcastAudience, string> = {
  all: "Semua pengguna",
  by_group: "Per grup reseller",
  by_handler: "Per admin handler",
  by_level: "Per level",
  specific: "Orang tertentu",
};

/** Batas panjang lembut (UI), selaras tampilan notif yang ringkas. */
export const TITLE_MAX = 80;
export const BODY_MAX = 500;

/**
 * Link internal yang aman dinavigasi: harus path relatif diawali "/" dan BUKAN
 * "//..." (yang ditafsirkan browser sebagai URL protokol-relatif → bisa keluar
 * domain). Cermin guard SQL di `admin_broadcast_notification`.
 */
export function isInternalLink(link: string | null | undefined): boolean {
  const v = (link ?? "").trim();
  return v.startsWith("/") && !v.startsWith("//");
}

/** Kembalikan link bersih bila internal, selain itu null. */
export function sanitizeLink(link: string | null | undefined): string | null {
  const v = (link ?? "").trim();
  return v && isInternalLink(v) ? v : null;
}

/** True bila waktu jadwal valid & berada di masa depan (→ dijadwalkan). */
export function isFutureSchedule(
  scheduledAt: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (!scheduledAt) return false;
  const t = new Date(scheduledAt).getTime();
  return Number.isFinite(t) && t > now.getTime();
}

/**
 * Saring penerima broadcast (preview). Buang: status ≠ active, tanpa akun login
 * (authUserId kosong). Cocokkan audiens. Dedupe per id. Urut stabil sesuai input.
 * Mengembalikan target lengkap supaya UI bisa menampilkan daftar pratinjau.
 */
export function filterBroadcastTargets(
  targets: BroadcastTarget[],
  filter: BroadcastFilter
): BroadcastTarget[] {
  const idSet =
    filter.audience === "specific"
      ? new Set((filter.customerIds ?? []).filter(Boolean))
      : null;
  const seen = new Set<string>();
  const out: BroadcastTarget[] = [];
  for (const t of targets) {
    if (!t.id || seen.has(t.id)) continue;
    if ((t.status ?? "").toLowerCase() !== "active") continue;
    if (!t.authUserId) continue;
    if (filter.audience === "by_group" && t.groupId !== filter.groupId) continue;
    if (filter.audience === "by_handler" && t.assignedAdminId !== filter.adminId)
      continue;
    if (filter.audience === "by_level" && t.customerLevel !== filter.level)
      continue;
    if (filter.audience === "specific" && !(idSet?.has(t.id) ?? false)) continue;
    seen.add(t.id);
    out.push(t);
  }
  return out;
}

/** Nama tampil penerima (fallback "Pelanggan"). */
export function targetDisplayName(t: BroadcastTarget): string {
  return (t.name ?? "").trim() || "Pelanggan";
}

/**
 * Validasi draft sebelum kirim. Mengembalikan daftar pesan error (kosong = valid).
 * Cermin guard RPC `admin_broadcast_notification`.
 */
export function validateBroadcastDraft(draft: BroadcastDraft): string[] {
  const errors: string[] = [];
  if (!draft.title.trim()) errors.push("Judul broadcast wajib diisi.");
  else if (draft.title.trim().length > TITLE_MAX)
    errors.push(`Judul maksimal ${TITLE_MAX} karakter.`);
  if (!draft.body.trim()) errors.push("Isi broadcast wajib diisi.");
  else if (draft.body.trim().length > BODY_MAX)
    errors.push(`Isi maksimal ${BODY_MAX} karakter.`);

  switch (draft.audience) {
    case "by_group":
      if (!draft.groupId) errors.push("Grup reseller wajib dipilih.");
      break;
    case "by_handler":
      if (!draft.adminId) errors.push("Admin handler wajib dipilih.");
      break;
    case "by_level":
      if (!draft.level) errors.push("Level wajib dipilih.");
      break;
    case "specific":
      if (!draft.customerIds || draft.customerIds.length === 0)
        errors.push("Pilih minimal satu penerima.");
      break;
    case "all":
      break;
    default:
      errors.push("Audiens tidak valid.");
  }

  const link = (draft.link ?? "").trim();
  if (link && !isInternalLink(link))
    errors.push('Link tujuan harus path internal (diawali "/").');

  return errors;
}

/** Draft kosong untuk inisialisasi form. */
export function emptyBroadcastDraft(): BroadcastDraft {
  return {
    audience: "all",
    title: "",
    body: "",
    link: "",
    groupId: null,
    adminId: null,
    level: null,
    customerIds: [],
    important: false,
    scheduledAt: "",
  };
}
